import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { getClientTimeZone } from './timezones'

dayjs.extend(utc)
dayjs.extend(timezone)

export type ScheduleWindow = {
  dayOfWeek: number // 0=Sun..6=Sat (in schedule.timezone)
  startTime: string // 'HH:mm' in schedule.timezone; '00:00' = start of day
  endTime: string // 'HH:mm' in schedule.timezone; '24:00' = end of day
  allowableCount?: number // optional, pass-through from legacy schema
}

export type Schedule = {
  timezone: string // IANA tz (e.g., 'America/New_York')
  windows: ScheduleWindow[]
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Parse 'HH:mm' into { h, m }. '24:00' returns { h: 24, m: 0 }. */
function parseTime(t: string): { h: number; m: number } {
  const [hStr, mStr] = t.split(':')
  return { h: parseInt(hStr, 10), m: parseInt(mStr, 10) }
}

/** Convert 'HH:mm' to minutes-since-midnight. '24:00' returns 1440. */
function timeToMinutes(t: string): number {
  const { h, m } = parseTime(t)
  return h * 60 + m
}

/** Convert minutes-since-midnight back to 'HH:mm'. 1440 returns '24:00'. */
function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// normalizeSchedule
// ---------------------------------------------------------------------------

/**
 * Reads either the old flat shape or the new per-window shape and returns a Schedule.
 *
 * Old shape: { timezone, daysOfWeek, timeRanges: [{ startTimeRange, endTimeRange, allowableCount? }] }
 * New shape: { schedule: { timezone, windows: ScheduleWindow[] } }
 */
export function normalizeSchedule(settings: any): Schedule {
  // New shape: has a nested `schedule` object with `windows`
  if (settings?.schedule?.windows !== undefined) {
    return {
      // Fall back to the client tz for the same reason the legacy branch below
      // does: Schedule.timezone is a required string, and a doc carrying
      // `windows` without a `timezone` would otherwise produce an undefined that
      // breaks convertScheduleTz and is rejected outright by Firestore writes.
      timezone: settings.schedule.timezone ?? getClientTimeZone(),
      windows: settings.schedule.windows.map((w: any) => ({ ...w })),
    }
  }

  // Old flat shape
  const tz: string = settings.timezone ?? getClientTimeZone()
  const daysOfWeek: number[] = settings.daysOfWeek ?? []
  const timeRanges: Array<{
    startTimeRange: string
    endTimeRange: string
    allowableCount?: number
  }> = settings.timeRanges ?? []

  const windows: ScheduleWindow[] = []

  for (const day of daysOfWeek) {
    for (const range of timeRanges) {
      const startTime = range.startTimeRange
      const endTime = range.endTimeRange
      const allowableCount = range.allowableCount

      // All-day sentinel: both times are '00:00' — means no time restriction
      if (startTime === '00:00' && endTime === '00:00') {
        windows.push({
          dayOfWeek: day,
          startTime: '00:00',
          endTime: '24:00',
          ...(allowableCount !== undefined ? { allowableCount } : {}),
        })
        continue // skip the overnight-split logic
      }

      // Overnight: startTime > endTime (e.g., '23:00' > '01:00')
      if (timeToMinutes(startTime) > timeToMinutes(endTime)) {
        // Split into two windows both anchored to `day`
        windows.push({
          dayOfWeek: day,
          startTime: '00:00',
          endTime,
          ...(allowableCount !== undefined ? { allowableCount } : {}),
        })
        windows.push({
          dayOfWeek: day,
          startTime,
          endTime: '24:00',
          ...(allowableCount !== undefined ? { allowableCount } : {}),
        })
      } else {
        windows.push({
          dayOfWeek: day,
          startTime,
          endTime,
          ...(allowableCount !== undefined ? { allowableCount } : {}),
        })
      }
    }
  }

  return { timezone: tz, windows }
}

// ---------------------------------------------------------------------------
// mergeAdjacentWindows
// ---------------------------------------------------------------------------

/**
 * Collapses duplicate or mergeable windows on the same day.
 * Two windows on the same day where one ends exactly where the other begins are merged.
 * Windows are NOT merged across different days.
 */
export function mergeAdjacentWindows(windows: ScheduleWindow[]): ScheduleWindow[] {
  // Group by dayOfWeek
  const byDay = new Map<number, ScheduleWindow[]>()
  for (const w of windows) {
    const existing = byDay.get(w.dayOfWeek)
    if (existing) {
      existing.push(w)
    } else {
      byDay.set(w.dayOfWeek, [w])
    }
  }

  const result: ScheduleWindow[] = []

  // Sort day keys
  const sortedDays = Array.from(byDay.keys()).sort((a, b) => a - b)

  for (const day of sortedDays) {
    const dayWindows = byDay.get(day)!
    // Sort by startTime (minutes)
    dayWindows.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))

    // Merge overlapping/adjacent ranges
    // Treat '24:00' as 1440 for comparison
    const merged: Array<{ startMins: number; endMins: number; allowableCount?: number }> = []

    for (const w of dayWindows) {
      const startMins = timeToMinutes(w.startTime)
      const endMins = timeToMinutes(w.endTime)

      if (merged.length === 0) {
        merged.push({ startMins, endMins, allowableCount: w.allowableCount })
      } else {
        const last = merged[merged.length - 1]
        if (startMins <= last.endMins) {
          // Overlapping or adjacent — extend
          last.endMins = Math.max(last.endMins, endMins)
          // If allowableCounts differ, drop it (can't merge meaningfully)
          if (last.allowableCount !== w.allowableCount) {
            last.allowableCount = undefined
          }
        } else {
          merged.push({ startMins, endMins, allowableCount: w.allowableCount })
        }
      }
    }

    for (const seg of merged) {
      const window: ScheduleWindow = {
        dayOfWeek: day,
        startTime: minutesToTime(seg.startMins),
        endTime: minutesToTime(seg.endMins),
      }
      if (seg.allowableCount !== undefined) {
        window.allowableCount = seg.allowableCount
      }
      result.push(window)
    }
  }

  return result
}

// ---------------------------------------------------------------------------
// convertScheduleTz
// ---------------------------------------------------------------------------

/**
 * Converts all windows from `schedule.timezone` to `targetTz`.
 * Returns a new Schedule with timezone = targetTz.
 */
export function convertScheduleTz(
  schedule: Schedule,
  targetTz: string,
  anchorDate?: Date | number
): Schedule {
  if (schedule.timezone === targetTz) {
    // Deep clone unchanged
    return {
      timezone: targetTz,
      windows: schedule.windows.map((w) => ({ ...w })),
    }
  }

  // Use the backtest start date (or today as fallback) as the DST anchor so the
  // displayed times reflect the UTC offset that was actually in effect during the run.
  let anchor = (anchorDate ? dayjs(anchorDate) : dayjs()).tz(schedule.timezone)
  while (anchor.day() !== 1) {
    anchor = anchor.subtract(1, 'day')
  }

  const convertedWindows: ScheduleWindow[] = []

  for (const win of schedule.windows) {
    // Advance from anchor Monday by dayOfWeek days
    // Monday=1 in dayjs, so offset = win.dayOfWeek - 1 (Sunday needs special handling)
    // anchor.day() === 1 (Monday), win.dayOfWeek: 0=Sun,1=Mon,...,6=Sat
    // Days to add: (win.dayOfWeek - 1 + 7) % 7
    const daysOffset = (win.dayOfWeek - 1 + 7) % 7
    const windowDate = anchor.add(daysOffset, 'day')

    // Build start datetime
    const { h: startH, m: startM } = parseTime(win.startTime)
    const startDt = windowDate.hour(startH).minute(startM).second(0).millisecond(0)

    // Build end datetime — handle '24:00' as start of next day
    let endDt: dayjs.Dayjs
    if (win.endTime === '24:00') {
      endDt = windowDate.add(1, 'day').startOf('day')
    } else {
      const { h: endH, m: endM } = parseTime(win.endTime)
      endDt = windowDate.hour(endH).minute(endM).second(0).millisecond(0)
    }

    // Convert to targetTz
    const startInTarget = startDt.tz(targetTz)
    const endInTarget = endDt.tz(targetTz)

    const startDow = startInTarget.day()
    const endDow = endInTarget.day()

    const startTimeStr = startInTarget.format('HH:mm')
    const endTimeStr = endInTarget.format('HH:mm')

    // Check if end is exactly midnight (00:00) — that means 24:00 of previous day
    const endIsMidnight = endTimeStr === '00:00'

    if (endIsMidnight) {
      // End landed exactly on midnight — treat as end-of-day (24:00) on startDow
      convertedWindows.push({
        dayOfWeek: startDow,
        startTime: startTimeStr,
        endTime: '24:00',
        ...(win.allowableCount !== undefined ? { allowableCount: win.allowableCount } : {}),
      })
    } else if (startDow === endDow) {
      // Same day, no midnight crossing
      convertedWindows.push({
        dayOfWeek: startDow,
        startTime: startTimeStr,
        endTime: endTimeStr,
        ...(win.allowableCount !== undefined ? { allowableCount: win.allowableCount } : {}),
      })
    } else {
      // Different days — midnight crossing: split into two windows
      convertedWindows.push({
        dayOfWeek: startDow,
        startTime: startTimeStr,
        endTime: '24:00',
        ...(win.allowableCount !== undefined ? { allowableCount: win.allowableCount } : {}),
      })
      convertedWindows.push({
        dayOfWeek: endDow,
        startTime: '00:00',
        endTime: endTimeStr,
        ...(win.allowableCount !== undefined ? { allowableCount: win.allowableCount } : {}),
      })
    }
  }

  return {
    timezone: targetTz,
    windows: mergeAdjacentWindows(convertedWindows),
  }
}

// ---------------------------------------------------------------------------
// summarizeScheduleWindows
// ---------------------------------------------------------------------------

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function summarizeScheduleWindows(schedule: Schedule): string {
  if (!schedule?.windows?.length) return 'No windows set'

  const daysPresent = Array.from(new Set(schedule.windows.map((w) => w.dayOfWeek))).sort()
  const dayLabel =
    daysPresent.length === 7 ? 'Every day' : daysPresent.map((d) => DAY_LABELS[d]).join(', ')

  const windowCount = schedule.windows.length
  const windowLabel = `${windowCount} window${windowCount === 1 ? '' : 's'}`

  return `${dayLabel} · ${windowLabel}`
}
